import { ColDef, GridApi, GridOptions } from 'ag-grid-community';
import 'ag-grid-enterprise';
import { DatePipe, DecimalPipe, formatPercent, PercentPipe } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SbTablePieContext } from '@models';
import { SbDataService, SbEventBusService, SbEventTopic } from '@services';
import { FormattingFunctions } from '../sb-ag-table/formatting-functions';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { SbChart } from '../../../shared/bb-charts/bb-chart/bb-chart.component';
import { filterNilValue } from '@datorama/akita';
import { StrictTitleCasePipe } from '../../../shared/pipes/strict-title-case.pipe';
import { LocalStorageService } from '../../../shared/services/local-storage.service';

@Component({
    selector: 'app-sb-table-pie',
    templateUrl: './sb-table-pie.component.html',
    styleUrls: ['./sb-table-pie.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class SbTablePieComponent
    extends BaseWidgetComponent
    implements OnInit, OnDestroy
{
    @Input() context: SbTablePieContext;

    private gridApi: GridApi;
    columnDefs: ColDef[];
    updateFlag = false;
    columnTypes: any;
    domLayout: string;
    gridStyle: any;
    gridOptions: GridOptions;
    rowData: any[];
    get allocationData$() {
        return this._allocationData$.pipe(filterNilValue());
    }

    get allocationData() {
        return this._allocationData$.value;
    }

    _allocationData$ = new BehaviorSubject(null);
    constructor(
        dateFormatter: DatePipe,
        numberFormatter: DecimalPipe,
        percentageFormatter: PercentPipe,
        dataService: SbDataService,
        localStorageService: LocalStorageService,
        eventBus: SbEventBusService,
        activatedRoute: ActivatedRoute,
        translateService: TranslateService,
        private cdr: ChangeDetectorRef,
        private strictTitleCase: StrictTitleCasePipe
    ) {
        super(dataService, localStorageService, eventBus, activatedRoute);
        this.responseHandler = this.setData;
        const formattingFunctions = new FormattingFunctions(
            dateFormatter,
            numberFormatter,
            percentageFormatter,
            translateService
        );
        this.columnTypes = formattingFunctions.columnTypes;
    }

    ngOnInit() {
        this.columnDefs = this.context.columnConfig;
        this.addCellClassRules(this.columnDefs);
        super.ngOnInit();
        this.registerEventListeners();
        this.gridOptions = { domLayout: 'autoHeight' };
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    addCellClassRules(colDefs: ColDef[]) {
        const labelColDef = colDefs.find((cd) => cd.field === 'label');
        if (labelColDef.hasOwnProperty('cellClassRules')) {
            labelColDef.cellClassRules['sb-drilldown-table-label'] = function (params) {
                return params.data ? params.data.hasDrillDown : false;
            };
        } else {
            labelColDef['cellClassRules'] = {
                'sb-drilldown-table-label': function (params) {
                    return params.data ? params.data.hasDrillDown : false;
                }
            };
        }
    }

    registerEventListeners() {
        // Size columns to fit will only work if the grid can calculate the width of the container (can't be done when display none).
        // Hence sizeColumnsToFit after events that reveal the table if hidden before.
        const sub = this.eventBus
            .getObservable(SbEventTopic.TabPanelTabChange)
            .subscribe((event) => {
                this.chart?.resize({ height: 160, width: 160 });
                //this.updateTable();
                setTimeout(() => {
                    if (this.gridApi) {
                        this.gridApi.sizeColumnsToFit();
                    }
                });
            });
        this.subscriptions.push(sub);
        const printPreviewSub = this.eventBus
            .getObservable(SbEventTopic.Print)
            .subscribe((event) => {
                if (event && event.payload && event.payload === 'Preview') {
                    setTimeout(() => {
                        if (this.gridApi) {
                            this.gridApi.sizeColumnsToFit();
                        }
                    });
                }
            });
        this.subscriptions.push(printPreviewSub);
    }

    chartData;
    setData(responseData: any) {
        const includeZeroValueSeries = this.context.includeZeroValues;
        this.chartData = responseData
            .filter((serie) => (includeZeroValueSeries ? true : serie.dataPoint))
            .reduce((acc, current) => {
                return {
                    ...acc,
                    [current.seriesName]: {
                        ...(acc[current.seriesName] || {}),
                        [current.drilldownId || current.displayName]: current.dataPoint
                    }
                };
            }, {});
        const value = Object.keys(this.chartData?.root || {});
        this._allocationData$.next({
            json: [this.chartData.root],
            keys: {
                value
            },
            classes: value.reduce(
                (acc, key, index) => ({
                    ...acc,
                    [key]: `group-color-${index + 1} value-color-1`
                }),
                {}
            )
        });
        this.updateTable();
    }

    chart: SbChart;
    allocationOptions = {
        data: {
            type: 'donut', // for ESM specify as: donut()
            onclick: (d, i, chart = this.chart) => {
                const data = this.chartData[d.id];
                if (data) {
                    chart.drilldown(d.name, data);
                    this.updateTable(data);
                }
            }
        },
        legend: {
            show: false
        },
        donut: {
            title: ' ',
            label: {
                show: false
            }
        },
        size: {
            height: 160,
            width: 160
        },
        transition: {
            duration: 0
        },
        tooltip: {
            format: {
                value: (value, ratio, id) => formatPercent(ratio, 'sv-SE', '0.0-2'),
                name: (value) => this.strictTitleCase.transform(value)
            },
            contents: {
                text: {
                    CLASS: [
                        '1',
                        '2',
                        '3',
                        '4',
                        '5',
                        '6',
                        '7',
                        '8',
                        '9',
                        '10',
                        '11',
                        '12',
                        '13',
                        '14',
                        '15',
                        '16',
                        '17',
                        '18',
                        '19'
                    ]
                },
                template: `{{<dl class="bb-target-group-color-{=CLASS} value-color-1 {=CLASS_TOOLTIP_NAME} bb-tooltip">
					<dt>{=NAME}</dt>
					<dd>{=VALUE}</dd>
			</dl>}}`
            }
        }
    };

    updateTable(data: any = this.chartData?.root || {}) {
        const totalValue = Object.keys(data).reduce((acc, key) => acc + data[key], 0);
        this.rowData = Object.keys(data).map((serie, index) => ({
            groupColumn: 'x',
            roundBullet: `table-bullet-color group-color-${index + 1} value-color-1`,
            label: serie,
            percentValue: totalValue && data[serie] ? data[serie] / totalValue : null,
            numericValue: data[serie],
            hasDrillDown: this.chartData[serie]
        }));
        this.cdr.detectChanges();
    }

    onGridReady(params) {
        this.gridApi = params.api;

        window.addEventListener('resize', function () {
            setTimeout(function () {
                if (params && params.api) {
                    params.api.sizeColumnsToFit();
                }
                this.chart?.flush();
            });
        });
    }

    onModelUpdated() {
        if (this.gridApi && this.columnDefs) {
            if (this.gridApi) {
                this.gridApi.sizeColumnsToFit();
            }
        }
    }
    onCellMouseOver(event) {
        if (event.data?.label) {
            this.chart.focus(event.data.label);
        }
    }
    onCellMouseOut(event) {
        this.chart.focus();
    }

    onRowClicked(event) {
        if (event.data.hasDrillDown) {
            const name = event.data.label;
            const data = this.chartData[name];
            if (data) {
                this.chart.drilldown(name, data);
                this.updateTable(data);
            }
        }
    }

    drillUp(data) {
        this.updateTable(data);
    }
}
